import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api";
import "./DashboardPedidos.css";

const POLL_MS = 8000;

function localDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtDate(value) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("es-CL", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
}

function fmtTime(value) {
  if (!value) return "--:--";
  return value;
}

function demandInfo(count) {
  if (count >= 10) return { key: "critical", label: "Muy alta demanda", pct: 100 };
  if (count >= 7) return { key: "danger", label: "Alta demanda", pct: 78 };
  if (count >= 4) return { key: "warning", label: "Demanda moderada", pct: 52 };
  return { key: "success", label: "Baja demanda", pct: Math.max(16, count * 16) };
}

function selectorDate(mode, customDate) {
  if (mode === "tomorrow") return localDate(1);
  if (mode === "custom") return customDate || localDate(0);
  return localDate(0);
}

export default function DashboardPedidos() {
  const [mode, setMode] = useState("today");
  const [customDate, setCustomDate] = useState(localDate(0));
  const [data, setData] = useState({ grupos: [], totalPedidos: 0, actualizadoEn: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);

  const fecha = selectorDate(mode, customDate);

  const cargar = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const { data: res } = await api.get("/api/pedidos/dashboard", { params: { fecha } });
      setData({
        grupos: Array.isArray(res?.grupos) ? res.grupos : [],
        pedidos: Array.isArray(res?.pedidos) ? res.pedidos : [],
        totalPedidos: Number(res?.totalPedidos || 0),
        actualizadoEn: res?.actualizadoEn || new Date().toISOString(),
      });
      setConnected(true);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo cargar el dashboard");
      setConnected(false);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fecha]);

  useEffect(() => {
    cargar();
    const id = setInterval(() => cargar({ silent: true }), POLL_MS);
    return () => clearInterval(id);
  }, [cargar]);

  const topSlots = useMemo(() => {
    return [...(data.grupos || [])]
      .sort((a, b) => Number(b.cantidad || 0) - Number(a.cantidad || 0))
      .slice(0, 4);
  }, [data.grupos]);

  const peak = topSlots[0]?.cantidad || 0;
  const lastUpdate = data.actualizadoEn
    ? new Date(data.actualizadoEn).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "--:--";

  return (
    <div className="dash-page">
      <header className="dash-header">
        <div className="brand">
          <img src="/logolasushiteria.png" alt="La Sushiteria" className="brand__logo" />
          <div>
            <h1 className="brand__title">Dashboard de pedidos</h1>
            <div className="brand__subtitle">Monitoreo por hora de retiro</div>
          </div>
        </div>
        <div className={`dash-live ${connected ? "is-online" : "is-offline"}`}>
          <span className="dash-live__dot" />
          <span>{connected ? "En tiempo real" : "Reconectando"}</span>
        </div>
      </header>

      <section className="dash-toolbar">
        <div className="dash-segmented" aria-label="Selector de dia">
          <button type="button" className={mode === "today" ? "active" : ""} onClick={() => setMode("today")}>Hoy</button>
          <button type="button" className={mode === "tomorrow" ? "active" : ""} onClick={() => setMode("tomorrow")}>Mañana</button>
          <button type="button" className={mode === "custom" ? "active" : ""} onClick={() => setMode("custom")}>Fecha</button>
        </div>
        <input
          type="date"
          className="dash-date"
          value={customDate}
          onChange={(e) => { setCustomDate(e.target.value); setMode("custom"); }}
        />
        <button type="button" className="dash-refresh" onClick={() => cargar()} disabled={loading}>
          Actualizar
        </button>
      </section>

      {error && <div className="dash-alert">{error}</div>}

      <div className="dash-layout">
        <main className="dash-board">
          <div className="dash-board__head">
            <div>
              <span className="dash-label">Fecha visualizada</span>
              <h2>{fmtDate(fecha)}</h2>
            </div>
            <div className="dash-total">
              <span>Total pedidos activos</span>
              <strong>{data.totalPedidos}</strong>
            </div>
          </div>

          {loading ? (
            <div className="dash-empty">Cargando horarios...</div>
          ) : !data.grupos?.length ? (
            <div className="dash-empty">No hay pedidos con hora de retiro para este dia.</div>
          ) : (
            <motion.div className="dash-grid" layout>
              <AnimatePresence>
                {data.grupos.map((slot) => {
                  const demand = demandInfo(Number(slot.cantidad || 0));
                  const relative = peak ? Math.max(demand.pct, Math.round((slot.cantidad / peak) * 100)) : demand.pct;
                  return (
                    <motion.article
                      key={slot.horaRetiro}
                      layout
                      initial={{ opacity: 0, y: 12, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{ duration: 0.22 }}
                      className={`dash-card dash-card--${demand.key}`}
                    >
                      <div className="dash-card__top">
                        <div>
                          <span className="dash-card__label">Hora de retiro</span>
                          <strong>{fmtTime(slot.horaRetiro)}</strong>
                        </div>
                        <span className="dash-card__live">En tiempo real</span>
                      </div>
                      <div className="dash-card__count">
                        <span>{slot.cantidad}</span>
                        <small>{slot.cantidad === 1 ? "pedido" : "pedidos"}</small>
                      </div>
                      <div className="dash-card__state">{demand.label}</div>
                      <div className="dash-activity" aria-hidden>
                        <motion.span
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, relative)}%` }}
                          transition={{ duration: 0.35 }}
                        />
                      </div>
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}
        </main>

        <aside className="dash-side">
          <section className="dash-side-card">
            <span className="dash-label">Resumen general</span>
            <div className="dash-side-total">{data.totalPedidos}</div>
            <p>Pedidos activos agrupados por hora de retiro.</p>
          </section>

          <section className="dash-side-card">
            <span className="dash-label">Horarios con mas demanda</span>
            <div className="dash-top-list">
              {topSlots.length ? topSlots.map((slot) => {
                const demand = demandInfo(Number(slot.cantidad || 0));
                return (
                  <div className={`dash-top-row dash-top-row--${demand.key}`} key={slot.horaRetiro}>
                    <strong>{slot.horaRetiro}</strong>
                    <span>{slot.cantidad}</span>
                  </div>
                );
              }) : (
                <p className="dash-muted">Sin horarios para mostrar.</p>
              )}
            </div>
          </section>

          <section className="dash-side-card">
            <span className="dash-label">Estado conexion POS</span>
            <div className={`dash-status ${connected ? "ok" : "bad"}`}>
              <span className="dash-live__dot" />
              {connected ? "Conectado" : "Sin conexion"}
            </div>
            <div className="dash-meta">
              <span>Ultima actualizacion</span>
              <strong>{lastUpdate}</strong>
            </div>
            <div className="dash-meta">
              <span>Fecha visualizada</span>
              <strong>{fecha}</strong>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
